import RefundTerms from '@v2/brands/topmags/components/RefundTerms';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * /help.
 *
 * A card per question, which is the institutional pages' section pattern — DiscountMags'
 * accessibility and copyright pages are the same shape. The questions were `<h3>`s under
 * the H1 in the legacy page and are the cards' `<h2>`s here; the words are unchanged.
 */
export default function Faq() {
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      {/* The legacy trail's words, not the rail's shorter label. */}
      <Breadcrumbs
        items={[{ label: 'Home', href: '/' }, { label: 'Frequently Asked Questions' }]}
      />

      <PageHeading eyebrow="Customer Information">Frequently Asked Questions</PageHeading>

      <div className="prose mt-6 flex flex-col gap-6">
        <ContentCard>
          <h2>How long will it take for my subscription to begin?</h2>
          <p>
            When you place your order with us, you will be provided with an estimated delivery date.
            Keep in mind; this estimated delivery date is based on an industry standard wait time
            between 8-12 weeks. When an order is processed, the publisher will set up your new
            account with a pre-printed label for the next issue. The reason the publisher does this
            is because there is already an issue in circulation. Magazine titles which are published
            more often usually arrive quicker than subscriptions that are published every other
            month or every season. If you have not received your magazine issue, and it has been
            past your estimated date, please contact us immediately so we may further assist you.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>How do I change my address with my order?</h2>
          {/*
           * This "contact us" was an absolute https://www.topmags.com/contact in the
           * legacy page while the identical link further down was relative. Same
           * destination in production, and the relative one is the only one that stays on
           * the site anywhere else. The words are unchanged.
           */}
          <p>
            You can change your address in two ways. You can utilize the publisher&apos;s customer
            service website (quickest way) or you may{' '}
            <a href={buildUrl('/contact')}>contact us</a> and we would be happy to change your
            address for you.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>Do you offer renewal subscriptions?</h2>
          <p>
            Yes we do sell our subscriptions with the option to renew. Even if you did not order
            from us originally, you can order a renewal subscription and it will add on to your
            current subscription. Please make sure before you complete your renewal order that all
            of the information you fill out matches exactly to your current subscription&rsquo;s
            magazine label. You do not need to worry about providing us with your account number.
            The only required information we need is the name and the address.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>What is Renew &amp; Save?</h2>
          <p>
            Renew &amp; Save is a convenient service that will automatically renew your eligible
            subscription(s) prior to the end of your subscription terms and will continually do so
            until you cancel. You may easily cancel at any time prior to the processing of your
            auto-renewal by signing into your account or contacting us. You may also cancel after
            the processing of your auto-renewal and receive a pro-rated refund for any undelivered
            issues.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>When will my eligible Renew &amp; Save subscriptions automatically renew?</h2>
          <p>
            If you order a subscription that has a term of 1 or more years, your auto-renewal will
            be processed 90 days prior to the expiration of your subscription term starting from the
            order date. If you order a subscription that has a term of less than 1 year, your
            auto-renewal will be processed 45 days prior to the expiration of your subscription term
            starting from the order date.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>How can I order a Gift Subscription?</h2>
          <p>
            You will have the option at check-out to specify each individual recipient by name and
            address.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>Do you offer international or expedited shipping?</h2>
          <p>
            Unfortunately at this time we currently do not ship orders outside of the United States.
            We also cannot expedite or postpone any delivery times as we do not have control over
            the delivery of the magazines.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>Do you ship to prison or APO addresses?</h2>
          <p>
            We can ship to both addresses so as long as the addresses are input correctly as
            specified by the institutions. For example, most prisons require that the inmate number
            is included right after the recipient&rsquo;s name.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>How can I fix my subscription if I am receiving duplicate issues?</h2>
          <p>
            You may have received duplicate issues in error due to mismatched information or an
            error during processing. This is an easy fix so please{' '}
            <a href={buildUrl('/contact')}>contact us</a> and we would be happy to combine any
            duplicate issues you may have received.
          </p>
        </ContentCard>

        {/* The same terms `/refund` publishes, and the same component, so the
            two cannot drift apart. */}
        <ContentCard>
          <h2>What is your cancellation/refund policy?</h2>
          <RefundTerms />
        </ContentCard>
      </div>
    </>
  );
}

Faq.layoutName = 'Layouts.Institutional';
